import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { getScrollContainer, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

/** KidSuper customisation: the sticky bar shows once the buy buttons are within this many px of the top or bottom of the screen. */
const STICKY_THRESHOLD_PX = 30;

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    // KidSuper customisation: assets/kidsuper-sticky-early.js may have shown the bar during
    // parsing. Start from the DOM state so the observer's first callback can hide it again if
    // the buy buttons are in view by the time this module runs.
    this.#isStuck = this.refs.stickyBar.dataset.stuck === 'true';

    // KidSuper (ADA note 13): the hidden bar is only faded and moved off screen, so without
    // `inert` screen readers read it and Tab reaches its controls. data-stuck is written from
    // several places (this file, kidsuper-sticky-early.js, the variant re-render), so inert
    // follows the attribute itself rather than each call site.
    this.#syncInert();
    this.#stuckObserver = new MutationObserver(this.#syncInert);
    this.#stuckObserver.observe(this.refs.stickyBar, { attributes: true, attributeFilter: ['data-stuck'] });

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  /** @type {MutationObserver | null} */
  #stuckObserver = null;

  /** Keeps the bar inert whenever it is off screen (data-stuck is not "true"). */
  #syncInert = () => {
    const { stickyBar } = this.refs;
    stickyBar.inert = stickyBar.dataset.stuck !== 'true';
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stuckObserver?.disconnect();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // KidSuper customisation: stock Horizon only shows the bar once the buy
      // buttons have been scrolled past. We show it as soon as they come within
      // STICKY_THRESHOLD_PX of the top or bottom of the screen (still fully visible),
      // in either direction, including below the fold on page load, and hide it once
      // they are back inside with that clearance. The root is inset by the threshold
      // and the observer fires at ratio 1, so "clear" means fully inside the inset area.
      const clear = entry.isIntersecting && entry.intersectionRatio >= 1;
      if (!clear && !this.#isStuck) {
        if (this.#isChatActive()) return;
        this.#showStickyBar();
      } else if (clear && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    }, {
      rootMargin: `-${STICKY_THRESHOLD_PX}px 0px -${STICKY_THRESHOLD_PX}px 0px`,
      threshold: [1],
    });

    // KidSuper customisation: stock Horizon hides the bar as soon as the footer
    // comes within 200px of the viewport. On this store's short product pages
    // that fires in the same tick as the buy buttons leaving, so the bar never
    // appears. Instead, hide only when the page is scrolled to the very bottom,
    // and bring it back as soon as the shopper scrolls up again.
    // The scroll container is the window on mobile and `.page-wrapper` on the desktop
    // squeeze layout (≥990px), where window scroll events never fire; read and listen
    // through the theme's scroll-container helpers, and rebind across the breakpoint.
    const onScroll = () => {
      const container = getScrollContainer();
      const atBottom = container.clientHeight + container.scrollTop >= container.scrollHeight - 2;

      if (atBottom && this.#isStuck) {
        this.#hiddenByBottom = true;
        this.#hideStickyBar();
      } else if (!atBottom && this.#hiddenByBottom) {
        const rect = buyButtonsBlock.getBoundingClientRect();
        // Same 40px threshold as the observer above.
        if (rect.top < STICKY_THRESHOLD_PX || rect.bottom > window.innerHeight - STICKY_THRESHOLD_PX) {
          this.#hiddenByBottom = false;
          if (!this.#isChatActive()) {
            this.#showStickyBar();
          }
        }
      }
    };

    const { signal } = this.#abortController;
    let scrollTarget = getScrollEventTarget();
    scrollTarget.addEventListener('scroll', onScroll, { passive: true, signal });
    scrollContainerMediaQuery.addEventListener(
      'change',
      () => {
        scrollTarget.removeEventListener('scroll', onScroll);
        scrollTarget = getScrollEventTarget();
        scrollTarget.addEventListener('scroll', onScroll, { passive: true, signal });
      },
      { signal }
    );

    // KidSuper customisation: because the bar can show on page load (buy buttons below the
    // fold), start observing only after the browser has restored any saved scroll position.
    // Safari restores it after `load`; observing before that showed the bar at scroll 0 and
    // hid it ~150ms later once the restored scroll brought the buttons into view, a visible
    // flicker on reload and back navigation. Two frames after `load` is past the restore in
    // both Safari (after load) and Chrome (during parsing).
    // Only reload, back/forward and #hash navigations scroll late, though. A fresh navigation
    // has nothing to restore, so observe straight away rather than waiting for every image
    // on the page to finish loading (on mobile that is often seconds after the page is usable).
    const observer = this.#buyButtonsIntersectionObserver;
    const observe = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!signal.aborted) observer.observe(buyButtonsBlock);
        });
      });
    };
    const navigationType = performance.getEntriesByType('navigation')[0]?.type;
    const scrollsLate =
      navigationType === 'reload' || navigationType === 'back_forward' || window.location.hash !== '';
    if (document.readyState === 'complete' || !scrollsLate) {
      observe();
    } else {
      window.addEventListener('load', observe, { once: true, signal });
    }
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    // KidSuper: honour Theme settings > Animations > Add to cart. Stock Horizon only
    // checks it in product-form.js and animates from the sticky bar regardless.
    const animationEnabled = this.#targetAddToCartButton.dataset.addToCartAnimation === 'true';
    const animatedElements = [this.refs.addToCartButton];

    if (animationEnabled) {
      const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

      flyToCartElement.classList.add('fly-to-cart--sticky');
      flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
      flyToCartElement.useSourceSize = 'true';
      flyToCartElement.source = this.refs.productImage;
      flyToCartElement.destination = cartIcon;

      document.body.appendChild(flyToCartElement);
      animatedElements.push(flyToCartElement);
    }

    await onAnimationEnd(animatedElements);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // KidSuper customisation: remember whether this add was triggered from the sticky bar
    // (puppet flag is still set at dispatch time) so a success can be announced to
    // kidsuper-custom.js, which swaps the bar to its post-add state.
    const fromStickyBar = this.#targetAddToCartButton?.dataset.puppet === 'true';

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise
        .then((result) => {
          if (fromStickyBar && !result?.detail?.didError) {
            this.dispatchEvent(new CustomEvent('kidsuper:sticky-added', { bubbles: true }));
          }
        })
        .catch(() => {})
        .finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
